import React, { useState } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, SectionTitle } from '../components/UI';
import useBreakpoint from '../useBreakpoint';
import '../DashboardPage.css';

/* -----------------------------------------------------------
   Data
   ----------------------------------------------------------- */
const STATS = [
  { icon: 'chat_bubble', label: 'Total Conversations', value: '12,842', change: '+12.5%', up: true, color: C.primary },
  { icon: 'cell_wifi', label: 'Active Chats', value: '843', change: '+2.4%', up: true, color: C.indigo },
  { icon: 'check_circle', label: 'Completed Chats', value: '11,999', change: '+14.1%', up: true, color: C.green },
  { icon: 'mail', label: 'Emails Collected', value: '4,205', change: '+8.2%', up: true, color: C.amber },
];

const HEALTH = [
  { label: 'NLP Engine', status: 'ONLINE', color: C.green },
  { label: 'Database Core', status: 'STABLE', color: C.green },
  { label: 'API Gateway', status: 'ONLINE', color: C.green },
];

const ALERTS = [
  { time: '10:45 AM', title: 'API Latency Spike', desc: 'Detected 450ms increase in response time across US-East-1 nodes.', color: C.red },
  { time: '09:12 AM', title: 'High-Value Lead', desc: "USR-8842 completed intent 'Enterprise Pricing' inquiry.", color: C.green },
  { time: 'YESTERDAY', title: 'Integration Sync Failure', desc: 'Salesforce webhook returned 401 Unauthorized for session SYNC-99.', color: C.primary },
  { time: 'YESTERDAY', title: 'System Restart Completed', desc: 'Scheduled maintenance for NLP Engine v2.4 deployment finished.', color: C.primary },
];

/* -- Rich session data for table + modal -- */
const RECENT = [
  {
    id: 'USR-7823', status: 'Active', time: '12:45 PM', msg: 'Can we schedule a proposal review?',
    name: 'Budi Santoso', email: 'budi.s@example.com', company: 'TechIndo Corp.',
    initials: 'BS', grad: `linear-gradient(135deg, ${C.teal}, ${C.indigo})`,
    channel: 'Web Widget', duration: '18m 32s', sentiment: 'Positive', intentDetected: 'Schedule Meeting',
    aiConfidence: 92, messagesCount: 12, attachments: 1, escalated: false,
    tags: [['Proposal', '#3b82f6'], ['Priority', '#f59e0b']],
    timeline: [
      { time: '12:27 PM', event: 'Session started via Web Widget' },
      { time: '12:28 PM', event: 'AI greeted user ? detected "Enterprise" intent' },
      { time: '12:33 PM', event: 'User asked about Q4 pricing breakdown' },
      { time: '12:38 PM', event: 'AI attached Integration_Roadmap_2023.pdf' },
      { time: '12:42 PM', event: 'User requested proposal review scheduling' },
      { time: '12:45 PM', event: 'AI proposed 3 available time slots' },
    ],
  },
  {
    id: 'USR-6101', status: 'Completed', time: '11:30 AM', msg: 'Thanks, that resolves my issue.',
    name: 'Siti Aminah', email: 'siti.aminah@govtech.id', company: 'GovTech Solutions',
    initials: 'SA', grad: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    channel: 'Web Widget', duration: '6m 14s', sentiment: 'Satisfied', intentDetected: 'Support ? Billing',
    aiConfidence: 97, messagesCount: 8, attachments: 0, escalated: false,
    tags: [['Resolved', '#16a34a']],
    timeline: [
      { time: '11:24 AM', event: 'Session started via Web Widget' },
      { time: '11:24 AM', event: 'User asked about invoice discrepancy' },
      { time: '11:26 AM', event: 'AI retrieved billing record #INV-20231014' },
      { time: '11:28 AM', event: 'AI explained prorated charge calculation' },
      { time: '11:30 AM', event: 'User confirmed issue resolved' },
      { time: '11:30 AM', event: 'Session marked as Completed ? CSAT: 5/5' },
    ],
  },
  {
    id: 'USR-9042', status: 'Active', time: '09:17 AM', msg: 'Need pricing details for enterprise.',
    name: 'Reza Pratama', email: 'reza.p@finserve.id', company: 'FinServe Indonesia',
    initials: 'RP', grad: 'linear-gradient(135deg, #a855f7, #ec4899)',
    channel: 'Mobile SDK', duration: '34m 08s', sentiment: 'Neutral', intentDetected: 'Enterprise Pricing',
    aiConfidence: 88, messagesCount: 19, attachments: 2, escalated: false,
    tags: [['Enterprise', '#7c3aed'], ['Pricing', '#3b82f6']],
    timeline: [
      { time: '08:43 AM', event: 'Session started via Mobile SDK' },
      { time: '08:44 AM', event: 'AI detected "Enterprise Pricing" intent' },
      { time: '08:50 AM', event: 'User requested feature comparison vs competitors' },
      { time: '09:02 AM', event: 'AI shared Pricing_Comparison_2023.pdf' },
      { time: '09:10 AM', event: 'User asked about volume discounts for 500+ seats' },
      { time: '09:17 AM', event: 'AI calculating custom enterprise quote' },
    ],
  },
  {
    id: 'USR-8500', status: 'Active', time: '06:12 AM', msg: 'API integration guide link?',
    name: 'Dian Wijaya', email: 'dian.w@freelance.co', company: 'Independent',
    initials: 'DW', grad: 'linear-gradient(135deg, #10b981, #3b82f6)',
    channel: 'WhatsApp', duration: '2m 45s', sentiment: 'Neutral', intentDetected: 'Documentation Request',
    aiConfidence: 95, messagesCount: 4, attachments: 0, escalated: false,
    tags: [['Quick Query', '#94a3b8']],
    timeline: [
      { time: '06:10 AM', event: 'Session started via WhatsApp Business' },
      { time: '06:10 AM', event: 'User asked for API integration documentation' },
      { time: '06:11 AM', event: 'AI provided link to docs.nieramind.ai/api/v2' },
      { time: '06:12 AM', event: 'User asked about rate limits ? awaiting reply' },
    ],
  },
  {
    id: 'USR-5511', status: 'Escalated', time: 'Yesterday', msg: 'The bot keeps giving wrong answers!',
    name: 'Lina Tan', email: 'lina.t@shopease.co', company: 'ShopEase',
    initials: 'LT', grad: 'linear-gradient(135deg, #f43f5e, #f59e0b)',
    channel: 'Web Widget', duration: '42m 11s', sentiment: 'Frustrated', intentDetected: 'Complaint ? Product Recs',
    aiConfidence: 34, messagesCount: 26, attachments: 1, escalated: true,
    tags: [['Escalation', '#ef4444'], ['Urgent', '#ef4444']],
    timeline: [
      { time: '09:00 AM', event: 'Session started via Web Widget' },
      { time: '09:05 AM', event: 'User reported incorrect product recommendations' },
      { time: '09:11 AM', event: 'AI detected inventory sync failure ? confidence dropped' },
      { time: '09:20 AM', event: 'User requested human agent escalation' },
      { time: '09:21 AM', event: 'Session escalated to Jordan Park (Senior Support)' },
      { time: '09:42 AM', event: 'Jordan confirmed root cause: stale inventory feed' },
    ],
  },
];

/* -----------------------------------------------------------
   Backdrop Modal (local)
   ----------------------------------------------------------- */
function Backdrop({ open, onClose, children, width = 620 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(30,24,18,.45)',
      backdropFilter: 'blur(4px)', zIndex: 9999,
      display: 'grid', placeItems: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.w, borderRadius: 18, width: '100%', maxWidth: width,
        maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,.18)',
      }}>
        {children}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   SVG Chart (unchanged)
   ----------------------------------------------------------- */
function ChartSVG() {
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const W = 680, H = 200, PX = 40, PY = 20, max = 320;
  const aD = [120, 90, 100, 130, 280, 250, 210];
  const cD = [80, 65, 70, 90, 160, 140, 120];
  const tX = (i) => PX + (i / 6) * (W - PX * 2);
  const tY = (v) => PY + (1 - v / max) * (H - PY * 2);
  const mp = (d) => d.map((v, i) => `${i ? 'L' : 'M'}${tX(i)},${tY(v)}`).join(' ');
  const ma = (d) => `${mp(d)} L${tX(6)},${H - PY} L${tX(0)},${H - PY} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(p => (
        <line key={p} x1={PX} x2={W - PX} y1={tY(max * p)} y2={tY(max * p)} stroke={C.bdr} strokeDasharray="4 4" strokeWidth={1} />
      ))}
      <path d={ma(cD)} fill="rgba(214,211,209,.25)" />
      <path d={mp(cD)} fill="none" stroke="#d6d3d1" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="aG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.primary} stopOpacity={0.22} />
          <stop offset="100%" stopColor={C.primary} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <path d={ma(aD)} fill="url(#aG)" />
      <path d={mp(aD)} fill="none" stroke={C.primary} strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round" />
      {aD.map((v, i) => (<circle key={i} cx={tX(i)} cy={tY(v)} r={4} fill={C.primary} stroke="#fff" strokeWidth={2} />))}
      {days.map((d, i) => (<text key={d} x={tX(i)} y={H + 18} textAnchor="middle" style={{ fontSize: 10, fontWeight: 600, fill: C.t3, fontFamily: "'Public Sans'" }}>{d}</text>))}
    </svg>
  );
}

/* -----------------------------------------------------------
   Main Dashboard Page
   ----------------------------------------------------------- */
export default function DashboardPage() {
  const [viewSession, setViewSession] = useState(null);
  const [csvExported, setCsvExported] = useState(false);
  const { isMobile } = useBreakpoint();

  /* -- #1 Export CSV -- */
  const handleExportCSV = () => {
    const headers = ['Session ID', 'Contact Name', 'Email', 'Company', 'Status', 'Time', 'Channel', 'Duration', 'Messages', 'Sentiment', 'Intent Detected', 'AI Confidence', 'Escalated', 'Last Message'];
    const rows = RECENT.map(r => [
      r.id, r.name, r.email, r.company, r.status, r.time, r.channel,
      r.duration, r.messagesCount, r.sentiment, r.intentDetected,
      `${r.aiConfidence}%`, r.escalated ? 'Yes' : 'No', `"${r.msg}"`,
    ]);
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nieramind_conversations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setCsvExported(true);
    setTimeout(() => setCsvExported(false), 2500);
  };

  /* -- Helper: status color -- */
  const statusStyle = (s) => {
    if (s === 'Active')    return { bg: C.pL, color: C.primary };
    if (s === 'Completed') return { bg: '#dcfce7', color: C.green };
    if (s === 'Escalated') return { bg: '#fef2f2', color: C.red };
    return { bg: '#f1eeec', color: C.t2 };
  };

  const s = viewSession; // shorthand for modal

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto' }}>
      <div className="dash-layout" style={{ display: 'flex' }}>
        <div className="dash-main" style={{ flex: 1, padding: '32px 32px 48px', minWidth: 0 }}>
          <SectionTitle sub="Monitoring real-time AI performance and conversation metrics.">Ops-Core Dashboard</SectionTitle>

          {/* Stat cards */}
          <div className="dash-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 18, marginBottom: 32 }}>
            {STATS.map((st, i) => (
              <Card key={i} style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${st.color}14`, display: 'grid', placeItems: 'center' }}>
                    <Icon name={st.icon} size={22} style={{ color: st.color }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: st.up ? C.green : C.red, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Icon name={st.up ? 'trending_up' : 'trending_down'} size={16} />{st.change}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: C.t3, fontWeight: 500 }}>{st.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1 }}>{st.value}</div>
              </Card>
            ))}
          </div>

          {/* Chart */}
          <Card className="dash-chart-card" style={{ padding: 28, marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700 }}>Conversations Over Time</h3>
                <p style={{ fontSize: 12, color: C.t3, marginTop: 3 }}>Active vs Completed sessions (last 7 days)</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: C.primary, display: 'inline-block' }} /> Active</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#d6d3d1', display: 'inline-block' }} /> Completed</span>
              </div>
            </div>
            <ChartSVG />
          </Card>

          {/* --- Recent Conversations Table --- */}
          <Card style={{ overflow: 'hidden' }}>
            <div className="dash-table-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px' }}>
              <h3 style={{ fontSize: 17, fontWeight: 700 }}>Recent Conversations</h3>
              <button onClick={handleExportCSV} className="dash-export-btn" style={{
                border: `1.5px solid ${csvExported ? C.green : C.primary}`,
                background: csvExported ? '#ecfdf5' : C.w,
                color: csvExported ? C.green : C.primary,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                padding: '8px 18px', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all .2s',
              }}>
                <Icon name={csvExported ? 'check_circle' : 'download'} size={16} />
                {csvExported ? 'Downloaded!' : 'Export CSV'}
              </button>
            </div>
            <div className="dash-table-scroll" style={{ overflowX: 'auto' }}>
              <table className="dash-responsive-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderTop: `1px solid ${C.bdr}`, borderBottom: `1px solid ${C.bdr}` }}>
                    {['USER / SESSION', 'STATUS', 'TIME', 'LAST MESSAGE', ''].map(h => (
                      <th key={h} style={{ padding: '10px 24px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {RECENT.map((r, i) => {
                    const sc = statusStyle(r.status);
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.bdr}`, transition: 'background .1s', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#faf8f7'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setViewSession(r)}>
                        <td data-label="User / Session" style={{ padding: '14px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: r.grad, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>{r.initials}</div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>{r.id}</div>
                              <div style={{ fontSize: 11, color: C.t3 }}>{r.name}</div>
                            </div>
                          </div>
                        </td>
                        <td data-label="Status" style={{ padding: '14px 24px' }}>
                          <Pill bg={sc.bg} color={sc.color}>{r.status}</Pill>
                        </td>
                        <td data-label="Time" style={{ padding: '14px 24px', color: C.t2 }}>{r.time}</td>
                        <td data-label="Last Message" className="dash-td-msg" style={{ padding: '14px 24px', color: C.t2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.msg}</td>
                        <td className="dash-td-action" style={{ padding: '14px 24px' }}>
                          <button onClick={(e) => { e.stopPropagation(); setViewSession(r); }} style={{ border: 'none', background: 'none', color: C.primary, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
                            View <Icon name="chevron_right" size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="dash-sidebar" style={{ width: 310, flexShrink: 0, padding: '32px 24px 48px 0', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Card style={{ padding: 22 }}>
            <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 16 }}>System Health</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {HEALTH.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{h.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: h.color }}>{h.status}</span>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: h.color, boxShadow: `0 0 6px ${h.color}88`, display: 'inline-block' }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card style={{ padding: 22 }}>
            <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 16 }}>Recent Alerts</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {ALERTS.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: a.color, marginTop: 4, flexShrink: 0, display: 'inline-block' }} />
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: C.t3, marginBottom: 3 }}>{a.time}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.5 }}>{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* -----------------------------------------------------------
         #2 Session Detail Modal
         ----------------------------------------------------------- */}
      <Backdrop open={!!viewSession} onClose={() => setViewSession(null)} width={isMobile ? '100%' : 640}>
        {s && (
          <>
            {/* Header */}
            <div className="dash-modal-header" style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: s.grad, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 18, fontWeight: 800 }}>{s.initials}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ fontSize: 17, fontWeight: 700 }}>{s.name}</h3>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, fontFamily: 'monospace' }}>{s.id}</span>
                  </div>
                  <p style={{ fontSize: 12, color: C.t2, marginTop: 2 }}>{s.company} ? {s.email}</p>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {s.tags.map(([t, col]) => (
                      <span key={t} style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${col}18`, color: col, textTransform: 'uppercase' }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer', marginTop: 4 }} onClick={() => setViewSession(null)} />
            </div>

            {/* KPI Grid */}
            <div className="dash-modal-kpi" style={{ padding: '20px 28px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                ['smart_toy', 'AI Confidence', `${s.aiConfidence}%`, s.aiConfidence >= 80 ? C.green : s.aiConfidence >= 50 ? C.amber : C.red],
                ['chat_bubble', 'Messages', `${s.messagesCount}`, C.indigo],
                ['timer', 'Duration', s.duration, C.teal],
                ['mood', 'Sentiment', s.sentiment, s.sentiment === 'Positive' || s.sentiment === 'Satisfied' ? C.green : s.sentiment === 'Frustrated' ? C.red : C.t2],
              ].map(([icon, label, val, color]) => (
                <div key={label} style={{ padding: 14, borderRadius: 12, background: '#faf9f8', border: `1px solid ${C.bdr}`, textAlign: 'center' }}>
                  <Icon name={icon} size={20} style={{ color, marginBottom: 6 }} />
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
                  <div style={{ fontSize: 10, color: C.t3, fontWeight: 600, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Session Info */}
            <div className="dash-modal-info" style={{ padding: '0 28px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                ['Channel', s.channel, 'language'],
                ['Intent Detected', s.intentDetected, 'psychology'],
                ['Status', s.status, 'info'],
                ['Escalated', s.escalated ? 'Yes ? Human Agent' : 'No', 'support_agent'],
                ['Attachments', `${s.attachments} file(s)`, 'attach_file'],
                ['Last Message', s.time, 'schedule'],
              ].map(([label, val, icon]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: '#faf9f8', border: `1px solid ${C.bdr}` }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${C.primary}10`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Icon name={icon} size={16} style={{ color: C.primary }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: label === 'Escalated' && s.escalated ? C.red : C.t1 }}>{val}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Confidence Bar */}
            <div className="dash-modal-confidence" style={{ padding: '0 28px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.t2 }}>AI Confidence Score</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: s.aiConfidence >= 80 ? C.green : s.aiConfidence >= 50 ? C.amber : C.red }}>{s.aiConfidence}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 99, background: '#f1eeec', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 99, transition: 'width .6s ease',
                  width: `${s.aiConfidence}%`,
                  background: s.aiConfidence >= 80 ? C.green : s.aiConfidence >= 50 ? C.amber : C.red,
                }} />
              </div>
            </div>

            {/* Session Timeline */}
            <div className="dash-modal-timeline" style={{ padding: '0 28px 24px' }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 14 }}>Session Timeline</h4>
              <div style={{ position: 'relative', paddingLeft: 20 }}>
                <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2, background: '#f1eeec' }} />
                {s.timeline.map((ev, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: i < s.timeline.length - 1 ? 16 : 0, position: 'relative' }}>
                    <div style={{
                      width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                      background: i === s.timeline.length - 1 ? C.primary : i === 0 ? C.green : '#d6d3d1',
                      border: `2px solid ${C.w}`, position: 'absolute', left: -19, top: 2,
                      boxShadow: i === s.timeline.length - 1 ? `0 0 0 3px ${C.pL}` : undefined,
                    }} />
                    <div style={{ marginLeft: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.t3, fontFamily: 'monospace' }}>{ev.time}</span>
                      <p style={{ fontSize: 12, color: C.t1, lineHeight: 1.5, marginTop: 1 }}>{ev.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="dash-modal-footer" style={{ padding: '16px 28px 24px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: C.t3, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="info" size={14} style={{ color: C.t3 }} />
                Session data is read-only and auto-synced.
              </span>
              <div className="dash-modal-footer-actions" style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setViewSession(null)} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer' }}>Close</button>
                <button style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <Icon name="open_in_new" size={14} /> Open Full Chat
                </button>
              </div>
            </div>
          </>
        )}
      </Backdrop>
    </div>
  );
}
